/**
 * MONSTER TOWN — INPUT ROUTER. ARCHITECT-OWNED, FROZEN. Additive only.
 *
 * THE ONE PLACE a tap becomes meaning. Nothing else in the game may add a
 * pointer listener to the canvas. Crews subscribe to `input:*` events.
 *
 * What it does with every gesture:
 *   1. Converts via `canvasNDC()` — canvas bounding rect, never window size.
 *   2. Raycasts the active scene for the ground point, the grid cell, and the
 *      placed instance under the finger (tolerant pick: a 3-year-old aiming at
 *      a chair and hitting the floor still gets the chair).
 *   3. Emits `input:tap` / `input:hold` / `input:drag*` / `input:pinch`.
 *   4. In PLAY mode it also does the obvious thing so simple crews get it free:
 *      tapped a thing -> `play:interactRequested`; tapped the floor ->
 *      `play:walkTo`. Auto-walk-then-interact is the monster crew's job: it
 *      listens for `play:interactRequested`, walks, then emits `play:interacted`.
 */

import { bus, type ResolvedInput } from '../core/events'
import { world } from '../core/scene'
import { pickCell, pickGround, pickInstance } from '../input/pick'
import type { CanvasPoint } from '../input/touch'
import { app, type GameContext } from './context'

function resolve(ctx: GameContext, p: CanvasPoint): ResolvedInput | null {
  const sceneId = world.activeSceneId
  if (sceneId === null) return null
  const node = world.require(sceneId)
  const cam = ctx.camera.camera
  return {
    point: { x: p.x, y: p.y, ndcX: p.ndcX, ndcY: p.ndcY },
    mode: app.mode,
    sceneId,
    ground: pickGround(cam, p, 0),
    cell: pickCell(cam, p, node, 0),
    instanceId: pickInstance(cam, p, node, ctx.canvas)?.instanceId ?? null,
  }
}

export function installRouter(ctx: GameContext): void {
  ctx.touch.onTap((p) => {
    const r = resolve(ctx, p)
    if (r === null) return
    // A real finger always wins the camera back. Only the screenshot harness
    // ever pins the framing, but nothing a kid does may leave it pinned.
    ctx.camera.releaseHold()
    bus.emit('input:tap', r)
    if (r.mode !== 'play') return
    if (r.instanceId !== null) {
      bus.emit('play:interactRequested', { instanceId: r.instanceId, sceneId: r.sceneId })
    } else if (r.ground !== null) {
      bus.emit('play:walkTo', { sceneId: r.sceneId, target: r.ground })
    }
  })

  ctx.touch.onHold((p) => {
    const r = resolve(ctx, p)
    if (r !== null) bus.emit('input:hold', r)
  })

  ctx.touch.onDragStart((e) => {
    const r = resolve(ctx, e.current)
    if (r !== null) bus.emit('input:dragStart', r)
  })
  ctx.touch.onDragMove((e) => {
    const r = resolve(ctx, e.current)
    if (r !== null) bus.emit('input:dragMove', r)
  })
  ctx.touch.onDragEnd((e) => {
    const r = resolve(ctx, e.current)
    if (r !== null) bus.emit('input:dragEnd', r)
  })

  ctx.touch.onPinch((e) => {
    ctx.camera.pinch(e.scale)
    bus.emit('input:pinch', { scale: e.scale })
  })

  // Desktop convenience only. Touch never produces hover.
  ctx.touch.onHover((p) => {
    if (app.mode !== 'build') return
    const r = resolve(ctx, p)
    if (r !== null) bus.emit('input:dragMove', r)
  })

  // The canvas gets pointer-capture on touch; tell the renderer to cap DPR.
  ctx.touch.onTap(() => {
    if (ctx.touch.isTouchDevice) ctx.renderer.setTouchMode(true)
  })
}
